import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isDefinedString } from "et3-type-guards";
import { useState } from "react";

import ET3Div from "../../core/et3-div";
import { withLiteObserverAndTheme } from "../../core/et3-enhanced-render";
import Et3Text from "../../core/et3-text";
import {
  LOGIN_FIELD_BORDER,
  LOGIN_FIELD_ERROR_SHADOW,
  LOGIN_FIELD_FOCUS_SHADOW,
  LOGIN_FIELD_GAP_CSS,
  LOGIN_FIELD_HEIGHT,
  LOGIN_FIELD_HEIGHT_CSS,
  LOGIN_FIELD_HINT_FONT_SIZE,
  LOGIN_FIELD_ICON_SIZE_CSS,
  LOGIN_FIELD_INPUT_CLASS,
  LOGIN_FIELD_INPUT_FONT_SIZE,
  LOGIN_FIELD_LABEL_FONT_SIZE,
  LOGIN_FIELD_LABEL_GAP_CSS,
  LOGIN_FIELD_PADDING_X_CSS,
  LOGIN_FIELD_RADIUS_CSS,
  LOGIN_FIELD_SHADOW,
  LOGIN_FORM_COLUMN_WIDTH,
  LOGIN_FULL_WIDTH_CSS,
  LOGIN_INPUT_MIN_FONT_PX,
  LOGIN_NO_MINIMUM_CSS,
  LOGIN_TEXT_MIN_FONT_PX,
  LOGIN_TRANSITION
} from "./login-constants";
import { getFlooredFontSize } from "./login-layout";

type TLoginTextField = {
  label: string;
  hint: string;
  icon: IconProp;
  type: "text" | "password";
  value: string;
  hasError: boolean;
  onChoose: (value: string) => void;
  onEnter: () => void;
};

const LoginTextField = withLiteObserverAndTheme<TLoginTextField>((props) => {
  const { label, hint, icon, type, value, hasError, onChoose, onEnter, theme } =
    props;
  const { colors, getFontSize, getFontType } = theme;
  const [isFocused, setIsFocused] = useState(false);

  const borderColor = (() => {
    switch (true) {
      case hasError:
        return colors.all.red;
      case isFocused:
        return colors.all.bondiBlue;
      default:
        return colors.all.shadeOfGray;
    }
  })();

  const fieldShadow = (() => {
    switch (true) {
      case hasError:
        return LOGIN_FIELD_ERROR_SHADOW;
      case isFocused:
        return LOGIN_FIELD_FOCUS_SHADOW;
      default:
        return LOGIN_FIELD_SHADOW;
    }
  })();

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={LOGIN_FORM_COLUMN_WIDTH}
      height={0}
      fDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{
        width: LOGIN_FULL_WIDTH_CSS,
        minWidth: LOGIN_NO_MINIMUM_CSS,
        gap: LOGIN_FIELD_LABEL_GAP_CSS
      }}
      onClick={() => {}}
    >
      <Et3Text
        fontFamily={"MEDIUM"}
        fontSize={LOGIN_FIELD_LABEL_FONT_SIZE}
        text={label}
        isSpan={false}
        color={colors.all.maastrichtBlue}
        style={{
          margin: 0,
          textAlign: "left",
          fontSize: getFlooredFontSize(
            getFontSize(LOGIN_FIELD_LABEL_FONT_SIZE),
            LOGIN_TEXT_MIN_FONT_PX
          )
        }}
      />
      <ET3Div
        isScrollable={false}
        isExtendable={false}
        withShadow={false}
        width={LOGIN_FORM_COLUMN_WIDTH}
        height={LOGIN_FIELD_HEIGHT}
        fDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        color={colors.all.white}
        style={{
          boxSizing: "border-box",
          width: LOGIN_FULL_WIDTH_CSS,
          minWidth: LOGIN_NO_MINIMUM_CSS,
          height: LOGIN_FIELD_HEIGHT_CSS,
          minHeight: LOGIN_NO_MINIMUM_CSS,
          borderRadius: LOGIN_FIELD_RADIUS_CSS,
          border: `${LOGIN_FIELD_BORDER} ${borderColor}`,
          boxShadow: fieldShadow,
          transition: LOGIN_TRANSITION,
          paddingLeft: LOGIN_FIELD_PADDING_X_CSS,
          paddingRight: LOGIN_FIELD_PADDING_X_CSS,
          gap: LOGIN_FIELD_GAP_CSS
        }}
        onClick={() => {}}
      >
        <FontAwesomeIcon
          icon={icon}
          color={isFocused ? colors.all.bondiBlue : colors.all.gray}
          style={{
            width: LOGIN_FIELD_ICON_SIZE_CSS,
            height: LOGIN_FIELD_ICON_SIZE_CSS,
            flexShrink: 0,
            transition: LOGIN_TRANSITION
          }}
        />
        <input
          type={type}
          className={LOGIN_FIELD_INPUT_CLASS}
          aria-label={label}
          value={value}
          onChange={({ target }) => onChoose(target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(event) => {
            switch (event.key) {
              case "Enter":
                onEnter();
                return;
              default:
                return;
            }
          }}
          style={{
            boxSizing: "border-box",
            alignSelf: "stretch",
            border: 0,
            padding: 0,
            backgroundColor: colors.all.transparent,
            color: colors.all.maastrichtBlue,
            fontFamily: getFontType("MEDIUM"),
            fontSize: getFlooredFontSize(
              getFontSize(LOGIN_FIELD_INPUT_FONT_SIZE),
              LOGIN_INPUT_MIN_FONT_PX
            ),
            width: LOGIN_FULL_WIDTH_CSS,
            minWidth: LOGIN_NO_MINIMUM_CSS
          }}
        />
      </ET3Div>
      {!isDefinedString(hint) ? null : (
        <Et3Text
          fontFamily={"LIGHT"}
          fontSize={LOGIN_FIELD_HINT_FONT_SIZE}
          text={hint}
          isSpan={false}
          color={colors.all.dimGray}
          style={{
            margin: 0,
            textAlign: "left",
            fontSize: getFlooredFontSize(
              getFontSize(LOGIN_FIELD_HINT_FONT_SIZE),
              LOGIN_TEXT_MIN_FONT_PX
            )
          }}
        />
      )}
    </ET3Div>
  );
});
export default LoginTextField;
